import React, { useState, useEffect, useCallback } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui';

/**
 * PWAInstallPrompt Component
 * 
 * Shows install prompt for PWA when user can install the app.
 * Uses beforeinstallprompt event to detect installability.
 *
 * @returns {JSX.Element | null} Install prompt or null
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show our custom install prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', () => {});
    };
  }, []);

  // Handle install click
  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  }, [deferredPrompt]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
  }, []);

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
      <div className="bg-primary text-primary-foreground rounded-lg shadow-lg p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <Download className="h-6 w-6 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-base">დააინსტალირე Deals Manager</h3>
              <p className="text-sm text-primary-foreground/80 mt-1">
                დააინსტალირე აპლიკაცია შენს მოწყობილობაზე სწრაფი წვდომისთვის
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0 text-primary-foreground hover:bg-primary-foreground/20"
            onClick={handleDismiss}
            aria-label="დახურვა"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleInstall}
            className="flex-1 bg-white text-slate-900 hover:bg-gray-100 font-semibold"
          >
            <Download className="h-4 w-4 mr-2" />
            დაინსტალირება
          </Button>
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="border-white/30 text-white hover:bg-white/20 bg-white/15 font-medium"
          >
            მოგვიანებით
          </Button>
        </div>
      </div>
    </div>
  );
}
